import type { components } from 'admination-react/api-schema'
import { ApiError, captureWriteIdentity, type WriteIdentity, fillPath, getJson, postForBlob, sendJson } from '@/core/api/http'
import { isSprachniveauAbweichung } from './spalteStand'

type Schemas = components['schemas']

export const ABSATZ_KEYS = [
  'vorbildung',
  'integrationskurs',
  'kletttest',
  'sprachlicher_ausdruck',
  'gesamtergebnis',
] as const

export type AbsatzKey = (typeof ABSATZ_KEYS)[number]

export type Absaetze = Record<AbsatzKey, string>

export type Quellen = Partial<Record<AbsatzKey, 'ki' | 'regel'>>


export type ErstberichtEntwurf = {
  absaetze: Absaetze
  quellen: Quellen
}

export type FehlendeEingabe = Schemas['FehlendeEingabe']

export type ErstberichtStand = Omit<Schemas['ErstberichtStandResult'], 'entwurf'> & { entwurf: ErstberichtEntwurf | null }

export type ErstberichtErzeugt = ErstberichtEntwurf

export async function getErstbericht(coachingId: string): Promise<ErstberichtStand> {
  const stand = await getJson<ErstberichtStand>(fillPath('/erstbericht/{coaching_id}', { coaching_id: coachingId }))
  if (!isSprachniveauAbweichung(stand.sprachniveau_abweichung)) {
    throw new Error("Die Antwort des Servers hatte nicht die erwartete Form.")
  }
  return stand
}

export function erzeugeErstbericht(coachingId: string, identity: WriteIdentity = captureWriteIdentity()): Promise<ErstberichtErzeugt> {
  const path = fillPath('/erstbericht/{coaching_id}/erzeugen', { coaching_id: coachingId })
  return sendJson<ErstberichtErzeugt>(path, 'POST', {}, undefined, identity)
}

export type ErstberichtBearbeitung = Pick<ErstberichtEntwurf, 'absaetze'>

export async function speichereErstberichtEntwurf(coachingId: string, bearbeitung: ErstberichtBearbeitung, identity: WriteIdentity = captureWriteIdentity()): Promise<void> {
  const path = fillPath('/erstbericht/{coaching_id}/entwurf', { coaching_id: coachingId })
  await sendJson<void>(path, 'PUT', bearbeitung, undefined, identity)
}

export function fetchErstberichtVorschau(coachingId: string, bearbeitung: ErstberichtBearbeitung, identity: WriteIdentity = captureWriteIdentity()): Promise<Blob> {
  const path = fillPath('/erstbericht/{coaching_id}/vorschau', { coaching_id: coachingId })
  return postForBlob(path, bearbeitung, 'application/pdf', undefined, identity)
}

export type ErstberichtAbgeschlossen = Schemas['ErstberichtAbschliessenResult']

export function bestaetigeErstbericht(
  coachingId: string,
  bearbeitung: ErstberichtBearbeitung,
  identity: WriteIdentity = captureWriteIdentity(),
): Promise<ErstberichtAbgeschlossen> {
  const path = fillPath('/erstbericht/{coaching_id}', { coaching_id: coachingId })
  return sendJson<ErstberichtAbgeschlossen>(path, 'POST', bearbeitung, undefined, identity)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function ablehnungsKoerper(error: unknown, status: number): Record<string, unknown> | null {
  if (!(error instanceof ApiError) || error.status !== status || !isRecord(error.detail)) return null
  return isRecord(error.detail.detail) ? error.detail.detail : null
}

function isFehlendeEingabe(value: unknown): value is FehlendeEingabe {
  return isRecord(value) && typeof value.eingabe === 'string' && typeof value.wo === 'string'
}

export type ErstberichtAblehnung = { meldung: string; fehlend: FehlendeEingabe[] }

function ablehnung(error: unknown, status: number): ErstberichtAblehnung | null {
  const body = ablehnungsKoerper(error, status)
  if (!body || typeof body.meldung !== 'string' || body.meldung.trim() === '') return null
  const fehlend = body.fehlend ?? []
  if (!Array.isArray(fehlend) || !fehlend.every(isFehlendeEingabe)) return null
  return { meldung: body.meldung, fehlend }
}

export function erstberichtAblehnung(error: unknown): ErstberichtAblehnung | null {
  return ablehnung(error, 422)
}

export function textdienstNichtVerfuegbar(error: unknown): boolean {
  return error instanceof ApiError && error.status === 503
}

export function konfliktMeldung(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null
  return ablehnung(error, 409)?.meldung ?? (error.detailText || null)
}

export type ErstberichtProblem = { feld: string; label: string; grund: string }

function isErstberichtProblem(value: unknown): value is ErstberichtProblem {
  return (
    isRecord(value) && typeof value.feld === 'string' && typeof value.label === 'string' && typeof value.grund === 'string'
  )
}

export function bestaetigenProbleme(error: unknown): { meldung: string; probleme: ErstberichtProblem[] } | null {
  const body = ablehnungsKoerper(error, 422)
  if (!body || typeof body.meldung !== 'string' || body.meldung.trim() === '') return null
  if (!Array.isArray(body.probleme) || body.probleme.length === 0 || !body.probleme.every(isErstberichtProblem)) return null
  return { meldung: body.meldung, probleme: body.probleme }
}

export type ErstberichtAbwesend = Schemas['ErstberichtAbwesend']

type AbwesendSignatur = Schemas['AbwesendSignaturRequest']

export function getErstberichtAbwesend(coachingId: string): Promise<ErstberichtAbwesend> {
  return getJson<ErstberichtAbwesend>(fillPath('/erstbericht/{coaching_id}/abwesend', { coaching_id: coachingId }))
}

export function fetchErstberichtAbwesendVorschau(coachingId: string): Promise<Blob> {
  const path = fillPath('/erstbericht/{coaching_id}/abwesend/vorschau', { coaching_id: coachingId })
  return postForBlob(path, {}, 'application/pdf')
}

export async function erstberichtAbwesendGegenzeichnen(coachingId: string, signaturPngBase64: string): Promise<void> {
  const path = fillPath('/erstbericht/{coaching_id}/abwesend/gegenzeichnen', { coaching_id: coachingId })
  await sendJson<unknown>(path, 'POST', { signatur: signaturPngBase64 } satisfies AbwesendSignatur)
}

export async function erstberichtNachholen(coachingId: string, signaturPngBase64: string): Promise<void> {
  const path = fillPath('/erstbericht/{coaching_id}/nachholen', { coaching_id: coachingId })
  await sendJson<unknown>(path, 'POST', { signatur: signaturPngBase64 } satisfies AbwesendSignatur)
}
